import * as React from "react";
import { FC, useState, useCallback } from "react";

import { useTestPageObject } from "../../Services/TestPageObjects/PageObjectExposure";
import { useMetrics } from "../../Internals/Metrics/MetricsProviderContext";
import { CREATE_OPTIONAL_BLOCK, DELETE_OPTIONAL_BLOCK } from "../../metricsAction";
import { MiniModalButtonsDirection } from "../../../Engine/Form/CustomizationContexts/HelperFunctionContext/UIUtil/ConfirmMiniModal/ConfirmMiniModal";

import { VisibleBlock } from "./VisibleBlock/VisibleBlock";
import { CreateBlockButton } from "./CreateBlockButton";
import { ConfirmDeletionModal } from "./ConfirmDeletionModal";

export interface OptionalBlockProps {
    isBlockInitiallyVisible: boolean;
    isReadOnly: boolean;
    legacyBindingPath: string;
    createTitle: string;
    blockTitle?: string;

    confirmOnDelete?: boolean;
    confirmText?: string;
    confirmHeaderText?: string;
    confirmButtonText?: string;
    discardButtonText?: string;
    confirmModalWidth?: string;
    confirmModalButtonsDirection?: MiniModalButtonsDirection;

    onDeleteBlock: () => Promise<void>;
    onCreateBlock: () => Promise<void>;
    requestDeletionConfirmation: () => Promise<boolean>;
}

type OptionalBlockEvent = typeof CREATE_OPTIONAL_BLOCK | typeof DELETE_OPTIONAL_BLOCK;

const enum BlockState {
    NotCreated,
    Created,
    BeforeDeletion,
}

export const OptionalBlock: FC<OptionalBlockProps> = props => {
    const [blockState, setBlockState] = useState(() =>
        props.isBlockInitiallyVisible ? BlockState.Created : BlockState.NotCreated
    );
    const visibleBlockState = blockState > BlockState.NotCreated;
    const blockCreated = props.isBlockInitiallyVisible && visibleBlockState;
    const beforeDeletion = blockState === BlockState.BeforeDeletion;

    const track = useMetrics();
    const sendMetric = useCallback(
        (event: OptionalBlockEvent) => {
            track(event, {
                bindingPath: props.legacyBindingPath,
                createTitle: props.createTitle,
                blockTitle: props.blockTitle,
            });
        },
        [track, props.legacyBindingPath, props.createTitle, props.blockTitle]
    );

    const setCreatedState = useCallback(() => setBlockState(BlockState.Created), [setBlockState]);

    const createBlock = useCallback(async () => {
        setCreatedState();
        await props.onCreateBlock();
        sendMetric(CREATE_OPTIONAL_BLOCK);
    }, [setCreatedState, props.onCreateBlock, sendMetric]);

    const deleteBlockAfterConfirmation = async () => {
        if (props.confirmOnDelete) {
            setBlockState(BlockState.BeforeDeletion);
            return;
        }

        await deleteBlock();
    };

    const deleteBlock = useCallback(async () => {
        setBlockState(BlockState.NotCreated);
        await props.onDeleteBlock();
        sendMetric(DELETE_OPTIONAL_BLOCK);
    }, [setBlockState, props.onDeleteBlock, sendMetric]);

    useTestPageObject(
        () => ({
            getCreateTitle: () => props.createTitle,
            getBlockTitle: () => props.blockTitle,
            getType: () => "OptionalBlock",
            createBlock: createBlock,
            deleteBlock: deleteBlockAfterConfirmation,
        }),
        // NOTE: blockState нужен в зависимостях, чтобы не отваливался TestPageObject при создании и удалении блока
        [blockState, props.createTitle, props.blockTitle, createBlock, deleteBlock]
    );

    const confirmDeletionModal = (
        <ConfirmDeletionModal
            text={props.confirmText}
            headerText={props.confirmHeaderText}
            confirmButtonText={props.confirmButtonText}
            discardButtonText={props.discardButtonText}
            width={props.confirmModalWidth}
            buttonsDirection={props.confirmModalButtonsDirection}
            onConfirm={deleteBlock}
            onDiscard={setCreatedState}
        />
    );

    const editingMode = !props.isReadOnly;

    return blockCreated ? (
        <>
            {beforeDeletion && confirmDeletionModal}
            <VisibleBlock
                showingDeleteButton={editingMode}
                title={props.blockTitle}
                onDeleteBlock={deleteBlockAfterConfirmation}>
                {props.children}
            </VisibleBlock>
        </>
    ) : (
        <>{editingMode && <CreateBlockButton label={props.createTitle} onClick={createBlock} />}</>
    );
};
